import { useState, useEffect } from "react";
import { Gift, Users, UserPlus, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { apiClient } from "@/shared/services/apiClient";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ReferralSystem } from "./ReferralSystem";

interface ReferralDiscount {
  activeReferrals: number;
  totalMonthsOfDiscount: number;
  monthsRemaining: number;
  monthsUsed: number;
  monthlySavings: number;
  totalSaved: number;
  totalPotentialSavings: number;
  firstConversionDate: string | null;
  estimatedEndDate: string | null;
  nextBillingAmount: number;
  referralsList: Array<{
    referee_name: string;
    referee_email: string;
    converted_at: string;
  }>;
  hasPendingRewards?: boolean;
  pendingMonths?: number;
  pendingAmount?: number;
}

export function ReferralDiscountDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [discountInfo, setDiscountInfo] = useState<ReferralDiscount | null>(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const { user, loading: authLoading, userDataLoaded } = useAuth();

  useEffect(() => {
    // Wait for user data to be fully loaded before querying
    // CRITICAL: userDataLoaded ensures user.id is available
    if (!authLoading && userDataLoaded && user?.id) {
      loadDiscountDetails();
    } else if (!authLoading && !user) {
      setLoading(false);
    }
  }, [authLoading, userDataLoaded, user]);

  const loadDiscountDetails = async () => {
    setLoading(true);
    try {
      if (!user || !user.id) {
        setLoading(false);
        return;
      }

      // ✅ NEW: Get subscriber data WITH reward tracking columns
      const subResponse = await apiClient.get<{
        ok: boolean;
        subscription: {
          subscribed: boolean;
          referral_id: string | null;
          referrer_reward_months: number | null;
          referrer_reward_expires_at: string | null;
          referrer_reward_coupons: Array<{
            coupon_id: string;
            months: number;
            applied_at: string;
            referral_id: string;
            total_months_after: number;
          }> | null;
        }
      }>(
        '/user/subscription'
      );

      if (!subResponse.ok || !subResponse.subscription) {
        setLoading(false);
        return;
      }

      const subscriber = subResponse.subscription;
      const isSubscribed = subscriber.subscribed;

      // ✅ NEW: Use referrer_reward_months from database
      const totalMonthsOfDiscount = subscriber.referrer_reward_months || 0;
      const expiresAt = subscriber.referrer_reward_expires_at ? new Date(subscriber.referrer_reward_expires_at) : null;
      const couponHistory = subscriber.referrer_reward_coupons || [];

      // SECURITY: Use backend API to get referral conversions for list display
      const response = await apiClient.get<{
        ok: boolean;
        totalReferrals: number;
        convertedReferrals: number;
        details: Array<{
          referee_name: string;
          referee_email: string;
          converted_at: string | null;
          created_at: string;
          subscribed: boolean;
          subscription_status: string;
        }>;
      }>('/subscribers/referral-conversions');

      // Filter converted referrals for display
      const referrals = response.ok ? response.details.filter(r => r.converted_at) : [];

      // Filter pending rewards (converted but coupon pending) - for non-subscribers
      const pendingReferrals = response.ok ? response.details.filter(r =>
        r.converted_at && r.subscription_status !== 'active'
      ) : [];

      const hasPendingRewards = pendingReferrals && pendingReferrals.length > 0;
      const pendingMonths = (pendingReferrals?.length || 0) * 3;
      const pendingAmount = pendingMonths * 33.33;

      // ✅ If no active discount months and user is not subscribed, show pending rewards
      if (totalMonthsOfDiscount === 0 && !isSubscribed && hasPendingRewards) {
        setDiscountInfo({
          activeReferrals: 0,
          totalMonthsOfDiscount: 0,
          monthsRemaining: 0,
          monthsUsed: 0,
          monthlySavings: 33.33,
          totalSaved: 0,
          totalPotentialSavings: 0,
          firstConversionDate: null,
          estimatedEndDate: null,
          nextBillingAmount: 99,
          referralsList: [],
          hasPendingRewards,
          pendingMonths,
          pendingAmount,
        });
        return;
      }

      // ✅ If no active discount months, don't show widget
      if (totalMonthsOfDiscount === 0) {
        setDiscountInfo(null);
        return;
      }

      // ✅ Calculate discount details from database columns
      const activeReferrals = referrals.length;

      // ✅ Calculate months used and remaining based on expiration date
      const now = new Date();
      const firstCoupon = couponHistory.length > 0 ? couponHistory[0] : null;
      const firstConversionDate = firstCoupon ? new Date(firstCoupon.applied_at) : null;

      let monthsRemaining = 0;
      let monthsUsed = 0;

      if (expiresAt) {
        // Calculate remaining months until expiration
        const monthsUntilExpiry = Math.max(0, Math.ceil(
          (expiresAt.getTime() - now.getTime()) / (30 * 24 * 60 * 60 * 1000)
        ));
        monthsRemaining = Math.min(monthsUntilExpiry, totalMonthsOfDiscount);
        monthsUsed = totalMonthsOfDiscount - monthsRemaining;
      } else {
        // No expiration date, assume all months remaining
        monthsRemaining = totalMonthsOfDiscount;
        monthsUsed = 0;
      }

      const monthlySavings = 33.33;
      const totalSaved = monthsUsed * monthlySavings;
      const totalPotentialSavings = totalMonthsOfDiscount * monthlySavings;

      // Next billing amount (base plan $99 - $33.33 discount = $65.67)
      const nextBillingAmount = monthsRemaining > 0 ? 65.67 : 99;

      setDiscountInfo({
        activeReferrals,
        totalMonthsOfDiscount,
        monthsRemaining,
        monthsUsed,
        monthlySavings,
        totalSaved: Math.round(totalSaved),
        totalPotentialSavings: Math.round(totalPotentialSavings),
        firstConversionDate: firstConversionDate?.toISOString() || null,
        estimatedEndDate: expiresAt?.toISOString() || null,
        nextBillingAmount,
        referralsList: referrals.map(r => ({
          referee_name: r.referee_name || 'Unknown',
          referee_email: r.referee_email,
          converted_at: r.converted_at || r.created_at,
        })),
        hasPendingRewards,
        pendingMonths,
        pendingAmount,
      });

    } catch (error) {
      console.error('Error loading discount details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            Referral Discounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (!discountInfo || discountInfo.monthsRemaining === 0) {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              Referral Discounts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              No active referral discounts. Refer friends to earn $100 credit!
            </p>
            <Button
              onClick={() => navigate('/refer')}
              className="w-full"
              variant="outline"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Refer a Friend
            </Button>
          </CardContent>
        </Card>

        {/* Referral Modal */}
        <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Refer Friends & Earn Credits</DialogTitle>
            </DialogHeader>
            <ReferralSystem />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      {/* ✅ NEW: Pending rewards widget for non-subscribers */}
      {discountInfo.hasPendingRewards && discountInfo.monthsRemaining === 0 && (
        <Card className="border-orange-200 bg-gradient-to-br from-orange-50/80 to-white">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              <CardTitle className="text-orange-900">Pending Referral Rewards</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white rounded-lg p-4 border border-orange-200">
              <p className="text-lg font-semibold text-gray-900 mb-2">
                You have <span className="text-orange-600">{discountInfo.pendingMonths} months</span> of referral discounts waiting!
              </p>
              <p className="text-sm text-gray-600">
                Total value: <strong className="text-orange-600">${discountInfo.pendingAmount?.toFixed(2)}</strong>
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-900 font-semibold mb-2">How it works:</p>
              <ul className="text-sm text-orange-800 space-y-1 list-disc list-inside">
                <li>Your friends signed up using your referral link</li>
                <li>They subscribed and got their $100 discount</li>
                <li>Now it's your turn to claim your rewards!</li>
              </ul>
            </div>

            <Button
              onClick={() => navigate('/billing')}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold"
            >
              Subscribe & Claim ${discountInfo.pendingAmount?.toFixed(0)} in Rewards
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-green-200 bg-gradient-to-br from-green-50/50 to-white">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-green-600" />
            <CardTitle className="text-green-900">Referral Discount</CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Simple message */}
          <div className="bg-white rounded-lg p-4 border border-green-100">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              You'll pay <span className="text-green-600">${discountInfo.nextBillingAmount}/month</span> for the next <span className="text-green-600">{discountInfo.monthsRemaining} months</span>
            </p>
            <p className="text-sm text-gray-600">
              Regular price: <span className="line-through">$99/month</span> • You save $33.33/month
            </p>
          </div>

          {/* Referral count */}
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-900">
                {discountInfo.activeReferrals} successful referral{discountInfo.activeReferrals !== 1 ? 's' : ''}
              </span>
            </div>
            <Badge className="bg-green-600 text-white">
              {discountInfo.monthsRemaining} months left
            </Badge>
          </div>

          {/* Tip */}
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-900">
              <strong>Tip:</strong> Refer more friends to extend your discount! Each referral adds 3 more months.
            </p>
          </div>

          {/* Refer a Friend Button */}
          <Button
            onClick={() => navigate('/refer')}
            className="w-full"
            variant="outline"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Refer Another Friend
          </Button>
        </CardContent>
      </Card>

      {/* Referral Modal */}
      <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Refer Friends & Earn Credits</DialogTitle>
          </DialogHeader>
          <ReferralSystem />
        </DialogContent>
      </Dialog>
    </>
  );
}
